import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { AppLoadingScreen } from 'src/components'
import { WorkspaceAccessDenied } from 'src/features/workspaces/components'
import { useSessionContext, useWorkspacePermissionsContext } from 'src/hooks'
import { AppLayoutShell } from 'src/layout/AppLayoutShell'
import { FocusedShell } from 'src/layout/FocusedShell'
import {
  isWorkspaceMemberOfSession,
  resolveWorkspaceAccessStatus
} from 'src/utils/workspaceMenuAccess'

import { paths } from './paths'

/**
 * Gates the entire `/workspaces/:id/*` subtree and owns the chrome for the
 * result. It sits above every workspace page — so pages that render their own
 * content without `WorkspacePageShell` (Home, Overview, Users) are covered
 * too, and a member without the permission never reaches a page's fetches.
 *
 * Two independent axes:
 * - **Membership** (from the session) → not a member gets the "wrong account"
 *   screen in the focused standalone shell (no sidebar/switcher — there is no
 *   workspace to frame). Offers signing in as a different profile.
 * - **Permissions** (`GET /organizations/permissions/user/`, served by
 *   `WorkspacePermissionsProvider`) → a member lacking the section's code gets
 *   the insufficient-permissions screen inside the full app shell, so they can
 *   still navigate to sections they do hold.
 */
export const WorkspaceAccessRoute = () => {
  const { id = '' } = useParams<{ id: string }>()
  const location = useLocation()
  const { user, isLoading } = useSessionContext()
  const { permissions, isLoading: isLoadingPermissions } =
    useWorkspacePermissionsContext()

  const workspaceId = Number(id)
  const idValid = id !== 'new' && Number.isFinite(workspaceId)

  if (!idValid) {
    return <Navigate to={paths.workspaces.root} replace />
  }

  // The session is normally settled by the time AuthGuard lets us through, but
  // guard against a flash of "wrong account" while `user` is still resolving.
  if (isLoading) {
    return <AppLoadingScreen />
  }

  // Membership first: a non-member must never wait on a permission request
  // that will come back empty anyway.
  if (!isWorkspaceMemberOfSession(user, workspaceId)) {
    return (
      <FocusedShell>
        <WorkspaceAccessDenied status="no-access" workspaceId={id} />
      </FocusedShell>
    )
  }

  // An unresolved permission list denies every gated section, so hold the
  // loading state rather than flashing "insufficient permissions".
  if (isLoadingPermissions || permissions === undefined) {
    return <AppLoadingScreen />
  }

  const status = resolveWorkspaceAccessStatus(
    user,
    workspaceId,
    location.pathname,
    permissions
  )

  if (status === 'insufficient-role') {
    return (
      <AppLayoutShell>
        <WorkspaceAccessDenied status="insufficient-role" workspaceId={id} />
      </AppLayoutShell>
    )
  }

  return (
    <AppLayoutShell>
      <Outlet />
    </AppLayoutShell>
  )
}
